import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AppState } from '../../../store/rootReducer';
import { IApplication } from '../../../Interfaces/Application/IApplication';
import { ApplicationDispatch } from '../../../store/application/types';
import { Radio, FormControlLabel, FormLabel, RadioGroup, List } from '@mui/material';
import { Splitter, SplitterOnChangeEvent, SplitterPaneProps } from '@progress/kendo-react-layout';
import { isSupportedDocument } from '../../../utils/files';
import Viewer from 'react-viewer';
import { ApplicationDocument } from '../../ApplicationDocument';
import {
    useQueryApplicationDocumentFile,
    useApplicationDocumentTypes,
    Document,
} from '../../../Data/ApplicationDocumentData';
import { getApplicant } from '../../../utils/appUtils';
import { Constants } from '../../../utils/constants';
import { LoadingPanel } from '@amsanalytics/react-shared-library';

interface IncomeVerificationPageProps {
    applicationId: string;
    documents: Document[];
    style?: CSSProperties;
    selectedIds?: number[];
    onSelectId?: (id: string) => void;
    itemId?: number;
}

const IncomeVerificationPage: React.FC<IncomeVerificationPageProps> = (props) => {
    const currentApp: IApplication = useSelector((state: AppState) => state.applications.editingApp!);
    const dispatch = useDispatch<ApplicationDispatch>();
    const primary = currentApp && getApplicant(currentApp, Constants.roleApplicantId, true);
    const cobuyer1 = currentApp && getApplicant(currentApp, Constants.roleCoApplicant1Id, true);
    const cobuyer2 = currentApp && getApplicant(currentApp, Constants.roleCoApplicant2Id, true);
    const selectedApplicantList = [primary, cobuyer1, cobuyer2];
    const [panes, setPanes] = useState<SplitterPaneProps[]>([
        { size: '75%', min: '25%', resizable: true },
        { min: '25%', resizable: true },
    ]);
    const [isDownloadingDocument, setIsDownloadingDocument] = useState(false);
    const [refresh, setRefresh] = useState({});
    const documents = [...(props.documents || [])];
    const applicationDocumentTypes = useApplicationDocumentTypes(dispatch);
    const [selectedDocument, setSelectedDocument] = useState<Document | undefined>();
    const { data: selectedDocumentObjectUrl } = useQueryApplicationDocumentFile(dispatch, selectedDocument?.documentId, selectedDocument?.name);
    const [selectedCustomerId, setSelectedCustomerId] = useState<string | undefined>(primary?.id);
    const selectedCustomer = selectedApplicantList.find(cc => cc?.id === selectedCustomerId);
    const previewPaneRef = useRef<HTMLDivElement>(null);

    const handleRadioButtonEvent = (customerId: string): void => {
        const foundCustomer = selectedApplicantList.find((cc) => cc?.id === customerId);
        setSelectedCustomerId(foundCustomer?.id);
        setSelectedDocument(undefined);
    };

    useEffect(() => {
        setRefresh({});
    }, [props.documents]);

    const MemoizedViewer = React.memo((props: { selectedDocument?: Document, selectedDocumentObjectUrl?: string, container: any }) => {
        return <Viewer
            key={props.selectedDocument?.name}
            visible={!!props.selectedDocumentObjectUrl}
            images={[{
                src: props.selectedDocumentObjectUrl || '',
                alt: props.selectedDocument?.name,
            }]}
            container={props.container}
            noClose
            noNavbar
            noImgDetails
            changeable={false}
            showTotal={false}
        />
    });

    useEffect(() => {
        if (selectedDocumentObjectUrl && isDownloadingDocument) {
            setIsDownloadingDocument(false);
        }
    }, [selectedDocumentObjectUrl]);

    const onDelete = useCallback((id: number) => {
        // Don't allow deletes from this page
    }, [documents]);


    return  (
        <div style={{ display: 'flex', flex: 1, flexDirection: 'column', height: 'calc(100dvh - 112px)', overflowY: 'hidden', overflowX: 'auto' }}>
            {cobuyer1 && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FormLabel component='legend' style={{ fontWeight: 'bold', marginRight: '1rem', marginLeft: '15px' }}>View Income Verification For: </FormLabel>
                    <RadioGroup
                        aria-label='customer credit toggle'
                        name='customer credit toggle'
                        value={selectedCustomer?.id}
                        onChange={(e) => {
                            handleRadioButtonEvent(e.target.value);
                        }}
                        row
                    >
                        <FormControlLabel
                            id='primaryRadio'
                            value={primary?.id}
                            control={<Radio color='primary' />}
                            label='Primary Applicant'
                            labelPlacement='end'
                        />
                        <FormControlLabel
                            id='coApplicant1Radio'
                            value={cobuyer1?.id}
                            control={<Radio color='primary' />}
                            label='Co-Applicant 1'
                            labelPlacement='end'
                        />
                        {cobuyer2 && (
                            <FormControlLabel
                                id='coApplicant2Radio'
                                value={cobuyer2?.id}
                                control={<Radio color='primary' />}
                                label='Co-Applicant 2'
                                labelPlacement='end'
                            />
                        )}
                    </RadioGroup>
                </div>
            )}
            <Splitter style={{ height: '100%' }} panes={panes} onChange={(event: SplitterOnChangeEvent) => setPanes(event.newState)}>
                {isDownloadingDocument && (
                  <LoadingPanel label="Downloading document..." style={{ position: 'absolute', top: '50%', left: '50%', zIndex: 1000, transform: 'translate(-50%, -50%)', backgroundColor: 'white', opacity: 0.8, width: '100%', height: '100%' }} />
                )}
                {!isDownloadingDocument && (
                <div ref={previewPaneRef} style={{ height: '100%' }}>
                    {isSupportedDocument(selectedDocument?.mimeType) ?
                        <MemoizedViewer
                            selectedDocument={selectedDocument}
                            selectedDocumentObjectUrl={selectedDocumentObjectUrl}
                            container={previewPaneRef?.current || undefined}

                        />
                        :
                        <iframe src={selectedDocumentObjectUrl}
                            width="100%"
                            height="99%"
                            style={{
                                maxHeight: "95vh",
                            }}
                        >
                            iframes not supported.
                        </iframe>
                    }
                </div>
                )}
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        maxHeight: "95vh",
                        height: '100%',
                    }}
                >
                    <div
                        style={{
                            overflowY: "auto",
                            flex: "1 1 auto",
                        }}
                    >
                        <List dense>
                            {applicationDocumentTypes && documents?.filter(d => d.customerId == selectedCustomer?.customerId).map(d =>
                                <ApplicationDocument
                                    key={`${d.name}-${d.mimeType}`}
                                    applicationId={props.applicationId}
                                    document={d}
                                    applicationDocumentTypes={applicationDocumentTypes}
                                    isEditing={(d.isNew && !d.file && !d.error)}
                                    isSelected={selectedDocument === d}
                                    onSelected={() => {
                                        // Do not re-download the document if it is already selected.
                                        if (selectedDocument?.id === d.id) {
                                            return;
                                        }
                                        setIsDownloadingDocument(true);
                                        setSelectedDocument(d);
                                    }}
                                    onDelete={onDelete}
                                    isReadonly={true}
                                    isCheckable={!!props.selectedIds}
                                    isChecked={props.selectedIds?.includes(d.id)}
                                    onChecked={props.onSelectId}

                                />
                            )}
                        </List>
                    </div>
                </div>
            </Splitter>
        </div>

    );
};

export default IncomeVerificationPage;
